/**
 * What the app and its own server agree on.
 *
 * The server (`server/assistant/`) holds every key — the model's, the voice's,
 * the listener's — and the app holds none. Everyone who opens the app, on any
 * device, gets the same assistant without anybody typing anything, because the
 * thing that needs the keys is the one place the keys are.
 *
 * Which *models* it uses is the family's choice, made on the Admin screen and
 * sent along with each request. The server only honours a model its keys can
 * actually use; anything else quietly gets the server's default.
 *
 * Both halves import this file, so a change to the shape is a type error on
 * whichever side forgot, not a silent mismatch on somebody's phone.
 */
import type { Lang } from '../../domain/types';
import type { ToolCall, ToolDef, Turn } from './types';

/** Where the server answers. Same origin as the app, so there is no CORS and no address to configure. */
export const ASSISTANT_API = '/api/assistant';

/**
 * The line in the system prompt where the part that changes on every question
 * begins.
 *
 * Everything above it — the instructions and the map of the app — is the same
 * for every question asked in the same mode, so the model caches it and only
 * the few lines below it are read fresh each time. That is the difference
 * between the model re-reading several thousand words before every answer and
 * not.
 */
export const CONTEXT_HEADING = '# WHERE THEY ARE RIGHT NOW';

export type AnswerProvider = 'anthropic' | 'openai';

/**
 * How long the model thinks before answering, in words that mean the same
 * thing whichever model it is. The server turns each into that model's own
 * setting.
 */
export const EFFORTS = ['quick', 'balanced', 'careful'] as const;
export type Effort = (typeof EFFORTS)[number];

/** What the server can do, as it reports it. `null` means that half has no key. */
export interface ServerStatus {
  answer: {
    /** Who answers when the family has not chosen a model. */
    provider: AnswerProvider;
    /** The server's default model. */
    model: string;
    /** The server's default thinking time. */
    effort: Effort;
    /** Every provider the server holds a key for — a chosen model may belong to any of them. */
    providers: AnswerProvider[];
  } | null;
  listen: { provider: 'openai'; model: string } | null;
  speak: {
    /** Who speaks English by default. */
    provider: 'speechify' | 'openai';
    /** Languages worth asking the server for; the rest go straight to the browser's own voice. */
    langs: Lang[];
    /** Who speaks Hebrew — Speechify only while its retiring multilingual model still answers. */
    hebrew: 'openai' | 'speechify' | null;
    /** The default voice model: an OpenAI speech model, or `speechify`. */
    model: string;
    /** The default voice, for knowing whether the assistant speaks as a man or a woman. */
    voice: string;
  } | null;
}

/** Every model the server's keys can use, for the Admin screen's choices. */
export interface ModelChoices {
  answer: Array<{ id: string; provider: AnswerProvider }>;
  listen: string[];
  /** OpenAI speech models, plus `speechify` when there is a Speechify key. */
  speak: string[];
  efforts: readonly Effort[];
}

export interface AskBody {
  system: string;
  turns: Turn[];
  tools: ToolDef[];
  /** The family's chosen model; empty or unusable means the server's default. */
  model?: string;
  effort?: Effort;
}

export interface AskResult {
  text: string;
  calls: ToolCall[];
  /**
   * The model's own record of this turn, handed back unchanged on the next
   * request. Opaque to the app. It carries the model's reasoning between tool
   * calls, which the API requires to be returned exactly as it was sent.
   */
  providerData?: unknown;
}

export interface WarmBody {
  system: string;
  tools: ToolDef[];
  model?: string;
}

export interface SpeakBody {
  text: string;
  lang: Lang;
  voice?: string;
  /** The family's chosen voice model. */
  model?: string;
}

/**
 * POST /api/assistant/issue — "Report a problem", filed as an issue in the
 * app's GitHub repository by the server (which holds GITHUB_TOKEN).
 */
export interface IssueBody {
  kind: 'fault' | 'idea' | 'question';
  title: string;
  detail?: string;
  /** Who sent it, by the name the family knows them by. */
  reporter?: string;
  /** Where they were and what was true — screen, language, device. Gathered, never asked for. */
  context?: Record<string, string | number | boolean | undefined>;
}

export interface IssueResult {
  number: number;
  url: string;
}
